<template>
  <div class="product-container">
    <div class="product" v-if="product">
      <div class="content-left">
        <div class="product-header">
          <h2>{{ item.label }}</h2>
          <div>
            <div class="fw-medium">{{ item.Location.name }}</div>
            <div>{{ item.Location.address }}</div>
            <p class="fw-medium">{{ item.capacity }}</p>
          </div>
        </div>
        <Scrollbar
          class="scrollbar--primary"
          :style="{
            'max-height': '50vh'
          }"
        >
          <div class="product-details">
            <div
              class="productDetails--img"
              :style="{
                background: `linear-gradient(135deg, ${event} 0%,${primary} 50%,${secondary} 100%)`
              }"
              v-if="product.image"
            >
              <img :src="product.image" :alt="product.name" />
            </div>
            <div class="productDetails--content">
              <div class="description">
                <p>
                  <span class="fw-medium">Details</span>
                </p>
                <p class="ql-editor" v-html="product.description"></p>

                <template v-if="!!product.ProductInclusions.length">
                  <p>
                    <span class="fw-medium">Inclusions:</span>
                  </p>
                  <ul class="inclusions">
                    <li
                      v-for="item in product.ProductInclusions"
                      :key="`inclusion-${item.Inclusion.id}`"
                    >
                      {{ item.Inclusion.name }}
                    </li>
                  </ul>
                </template>
              </div>
            </div>
          </div>
        </Scrollbar>
      </div>
      <div class="content-right">
        <div class="d-md-none mt-30"></div>
        <Select
          class="term-select"
          name="term"
          placeholder="Select Term"
          :options="terms"
          v-model="term"
          :selected="term"
          :style="{ color: primary }"
          @input="term => handleProductDetails(term.value)"
          ref="termSelect"
        />
        <div class="productDetails--process">
          <FormSchedule
            :term="term"
            :inclusions="inclusions"
            :orderIndex="orderIndex"
            :order="order"
            :productLinkId="
              product.ProductLink
                ? product.ProductLink.productCollectionId
                : null
            "
            :productLinkCollection="product.productLinkCollection"
            :productId="productId"
            :item="item"
            @back="onClickBack"
            @submit="onClickSubmit"
          />
        </div>
      </div>
    </div>
    <Spinner v-else :theme="primary" />
  </div>
</template>

<script>
import Scrollbar from "@/components/Scrollbar";
import Spinner from "@/components/Spinner";
import FormSchedule from "@/components/booking/FormSchedule";
import axios from "@/helpers/axios";
import Select from "@/components/Select";

export default {
  components: {
    FormSchedule,
    Spinner,
    Scrollbar,
    Select
  },
  props: {
    productId: {
      type: [Number, String],
      default: null
    },
    orderIndex: {
      type: [Number, String],
      default: null
    },
    order: {
      type: Object,
      default: null
    },
    item: {
      type: Object,
      default: null
    }
  },
  computed: {
    primary() {
      return this.$store.state.app.scheme.primary;
    },
    secondary() {
      return this.$store.state.app.scheme.secondary;
    },
    event() {
      return this.$store.state.app.scheme.event;
    },
    danger() {
      return this.$store.state.app.scheme.danger;
    },
    terms() {
      return this.sortTerm(this.product.Terms)
        .filter(item => item.status === 2)
        .map(item => ({
          label: item.name,
          value: item.id
        }));
    }
  },
  data() {
    return {
      startTime: "",
      endDate: "",
      endTime: "",
      config: {
        altFormat: "F j, Y",
        altInput: true
      },
      product: null,
      term: null,
      inclusions: null
    };
  },
  mounted() {
    this.fetchProduct();
  },
  methods: {
    onClickBack() {
      this.$emit("back");
    },
    onClickSubmit(orderPackage) {
      const term = this.term;
      this.$emit("next", { orderPackage, term });
    },
    onClickUpdate(orderPackage) {
      const term = this.term;
      this.$emit("update", { orderPackage, term });
    },
    handleProductDetails(termId) {
      const { Terms: terms, ProductInclusions: inclusions } = this.product;
      const term = terms.find(item => item.id === termId);
      this.term = { ...term, label: term.name, value: term.id };
      this.inclusions = inclusions;
    },
    sortTerm(terms) {
      const termKeys = ["hour", "day", "week", "month", "year"];
      const sortedTerms = [];
      termKeys.forEach(tKey => {
        sortedTerms.push(
          ...terms
            .filter(t => t.calculatedBy === tKey)
            .sort((a, b) => a.calculatedValue - b.calculatedValue)
        );
      });
      return sortedTerms;
    },
    fetchProduct() {
      axios.get(`/products/${this.productId}`).then(response => {
        const { data: product } = response.data;
        this.product = product;
        this.setDefaultTerm();
      });
    },
    setDefaultTerm() {
      if (this.order) {
        this.handleProductDetails(this.order.Term.id);
      } else if (this.product.Terms && this.terms.length > 0) {
        const selectedTerm = this.terms.find(t => t.value);
        this.handleProductDetails(selectedTerm.value);
      }
    }
  }
};
</script>

<style lang="scss">
.product-container {
  color: #4c4c4c;
  p {
    margin-bottom: 1rem;
  }

  h2 {
    color: #232323;
    font-size: $font-size-lg;
    margin-bottom: 1.5rem;
    position: relative;

    @media (min-width: $screen-lg) {
      margin-bottom: 2.5rem;
    }
  }

  .content {
    &-left,
    &-right {
      flex: 100%;
      padding-left: 2.25rem;
      padding-right: 2.25rem;

      @media (min-width: $screen-md) {
        padding-left: 1rem;
        padding-right: 1rem;
      }
    }

    &-left {
      @media (min-width: $screen-md) {
        flex: 0 0 50%;
      }
    }

    &-right {
      @media (min-width: $screen-md) {
        flex: 0 0 50%;
      }
    }
  }

  .product {
    display: flex;
    flex-flow: row wrap;
    margin-left: -2.25rem;
    margin-right: -2.25rem;

    @media (min-width: $screen-md) {
      margin-left: -1rem;
      margin-right: -1rem;
    }

    &-details {
      display: none;

      @media (min-width: $screen-md) {
        display: block;
        max-width: 90%;
      }
    }
  }

  .productDetails {
    color: #505050;
    &--img {
      width: 100%;
      height: 8.75rem;
      margin-bottom: 1.5rem;
      overflow: hidden;
      border-radius: 5px;
      img {
        height: inherit;
        width: 100%;
        object-fit: cover;
      }
    }
    &--content {
      .title {
        margin-top: 30px;
        font-size: 18px;
        line-height: 24px;
      }
      .subtitle {
        margin-top: 20px;
        margin-bottom: 30px;
        p {
          font-size: 14px;
          line-height: 15px;
          margin-bottom: 0;
        }
        span {
          font-size: 12px;
          color: #929292;
        }
      }
      .description {
        font-size: 16px;
        line-height: 1.3125;
        & /deep/ img {
          max-width: 100%;
        }
      }
      .ql-editor {
        padding: 0px;
        margin: 0 0 1.5rem 0;
      }
    }
    &--process {
      .header {
        font-size: 16px;
        margin-bottom: 20px;
        @media (max-width: 991px) {
          margin-top: 20px;
        }
      }
      .subheader {
        button {
          display: inline-block;
          font-size: $font-size-sm;
          margin-right: 10px;
          margin-top: 10px;
          padding: 8px 14px;
          width: auto;
        }
      }
      .description {
        margin-top: 30px;
      }
      .inputs {
        margin-bottom: 100px;
        margin-top: 50px;
        p {
          font-size: 12px;
        }
      }
    }
    &--buttons {
      float: right;
      button {
        margin-right: 10px;
      }
    }
  }

  .inclusions {
    line-height: 1.75;
    padding-left: 1.875em;
  }

  .term-select {
    margin: 0 auto 1rem;

    &.open {
      .dropdown-toggle {
        border-color: $brand-primary-v2 !important;
      }
      .dropdown-menu {
        margin-top: 0.5rem;
      }
    }

    .dropdown-toggle {
      height: 40px;
      border-radius: 4px !important;
      border-color: #ccc !important;
      color: #4c4c4c !important;

      .vs__selected-options {
        padding-top: 0.3125rem;
        padding-bottom: 0.3125rem;

        span {
          color: var(--primary, curentColor);
          font-size: 0.875rem;
          margin: 0;
          padding: 0.1875rem 0.25rem;
          line-height: 1.42857143;
        }
      }
    }
  }
}
</style>
